import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useForecast } from "../lib/api";
import { degreesToCompass } from "../lib/scorer";
import type { ScoredHour } from "../lib/types";
import { CompassRing } from "../components/map/CompassRing";
import { SpotDetailMap } from "../components/map/SpotDetailMap";
import styles from "./SpotMapPrototypePage.module.css";

interface SpotMapPrototypePageProps {
  /** From wouter route params: /spot-map-proto/:id */
  params: { id: string };
}

/**
 * Prototype: spot-detail map view with a single uniform wind direction
 * driven by the selected forecast hour. No map controls — selecting a
 * different hour re-renders the particle layer to match the new direction.
 */
export function SpotMapPrototypePage({ params }: SpotMapPrototypePageProps) {
  const [, setLocation] = useLocation();
  const { data: forecast, isLoading, error } = useForecast();
  const [selectedHourIndex, setSelectedHourIndex] = useState(0);

  const spot = forecast?.spots.find((s) => s.id === params.id);

  // Flatten the first 2 days of forecast hours into one strip, similar to
  // what the existing detail page does. Defaults to "today + tomorrow".
  const hours = useMemo<ScoredHour[]>(() => {
    if (!spot) return [];
    const out: ScoredHour[] = [];
    for (const day of spot.days.slice(0, 2)) {
      if (day.level === "skip") continue;
      for (const h of day.hours) out.push(h);
    }
    return out;
  }, [spot]);

  // Auto-select the "now" hour the first time the forecast lands.
  // After that, the user's clicks own the state.
  const didAutoSelect = useRef(false);
  useEffect(() => {
    if (didAutoSelect.current || hours.length === 0) return;
    const idx = hours.findIndex((h) => h.isNow);
    if (idx >= 0) setSelectedHourIndex(idx);
    didAutoSelect.current = true;
  }, [hours]);

  const selectedHour = hours[selectedHourIndex] ?? null;

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={() => setLocation("/dev")}>
            ← Dev
          </button>
          <span className={styles.spotMeta}>Loading forecast…</span>
        </div>
      </div>
    );
  }

  if (error || !spot) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={() => setLocation("/dev")}>
            ← Dev
          </button>
          <span className={styles.spotMeta}>
            {error ? `Error: ${(error as Error).message}` : `Spot "${params.id}" not found`}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => setLocation("/dev")}>
          ← Dev
        </button>
        <div>
          <div className={styles.spotName}>{spot.name}</div>
          <div className={styles.spotMeta}>
            {spot.area} · {spot.coords.lat.toFixed(3)}, {spot.coords.lon.toFixed(3)}
          </div>
        </div>
      </div>

      <div className={styles.mapContainer}>
        <SpotDetailMap
          coords={spot.coords}
          windDirectionDeg={selectedHour?.dirDeg ?? null}
          windSpeedMs={selectedHour?.speedMs ?? null}
          zoom={11}
        />

        {/* Compass-ring overlay — shows ideal/secondary direction sectors and a
            rotating indicator for the currently rendered wind direction. */}
        <div className={styles.compassOverlay}>
          <CompassRing
            windDirectionDeg={selectedHour?.dirDeg ?? null}
            windSpeedMs={selectedHour?.speedMs ?? null}
            ideal={spot.ideal}
            secondary={spot.secondary}
          />
        </div>

        {selectedHour && (
          <div className={styles.windReadout}>
            <span>
              {new Date(selectedHour.time).toLocaleString(undefined, {
                weekday: "short",
                hour: "numeric",
              })}
            </span>
            <span className={styles.windReadoutValue}>
              {(selectedHour.speedMs * 1.944).toFixed(1)} kn ·{" "}
              {degreesToCompass(selectedHour.dirDeg)} ({Math.round(selectedHour.dirDeg)}°)
            </span>
            <span className={styles.idealLabel}>
              Ideal: {spot.ideal.dirs.join("/")} @ {spot.ideal.speed_min}–{spot.ideal.speed_max} m/s
            </span>
          </div>
        )}
      </div>

      <div className={styles.hourStrip}>
        {hours.map((h, i) => {
          const active = i === selectedHourIndex;
          const date = new Date(h.time);
          const label = date.toLocaleString(undefined, {
            weekday: "short",
            hour: "numeric",
          });
          return (
            <button
              type="button"
              key={h.time}
              className={`${styles.hourCell} ${active ? styles.hourCellActive : ""}`}
              onClick={() => setSelectedHourIndex(i)}
            >
              <span className={styles.hourTime}>{label}</span>
              <span className={styles.hourSpeed}>
                {(h.speedMs * 1.944).toFixed(0)} kn
              </span>
              <span className={styles.hourDir}>{degreesToCompass(h.dirDeg)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
